import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Container } from '@mui/material';
import { parse } from 'date-fns';

// Import the appointment service
import { 
  getAllAppointments, 
  getAppointmentsByDate, 
  getAppointmentsByDateAndHour 
} from './appointmentService';

// Import custom components
import AppointmentsHeader from './components/AppointmentsHeader';
import AppointmentsFilter from './components/AppointmentsFilter';
import AppointmentsTable from './components/AppointmentsTable';

// Import contexts and utilities
import { useAuth } from '../../context/AuthContext';
import { 
  getStatusColor, 
  formatAppointmentPageTitle 
} from './appointmentUtils';
import { getCompanyAppointmentUrlSegment } from '../../shared/booking/appointments/companyAppointmentRouting';

const AppointmentsView = () => {
  const { date, hour } = useParams();
  const navigate = useNavigate();
  const { companyData } = useAuth();
  
  // State for appointments and loading
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [appointments, setAppointments] = useState([]);
  const [filteredAppointments, setFilteredAppointments] = useState([]);
  
  // State for filtering and pagination
  const [searchTerm, setSearchTerm] = useState('');
  const [filterCalendar, setFilterCalendar] = useState('all');
  const [filterEmployee, setFilterEmployee] = useState('all');
  const [filterStatus, setFilterStatus] = useState('all');
  const [page, setPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [availableCalendars, setAvailableCalendars] = useState([]);
  const [availableEmployees, setAvailableEmployees] = useState([]);
  
  // State for sorting
  const [orderBy, setOrderBy] = useState('start');
  const [order, setOrder] = useState('asc');
  
  // Ref to track if we've already fetched data
  const dataFetchedRef = useRef(false);
  
  // Parse the date and hour from URL parameters if provided
  const parsedDate = useMemo(() => 
    date ? parse(date, 'yyyy-MM-dd', new Date()) : null
  , [date]);
  
  const parsedHour = useMemo(() => 
    hour ? parseInt(hour, 10) : null
  , [hour]);
  
  // Define fetchAppointments with useCallback to prevent recreation on each render
  const fetchAppointments = useCallback(async () => {
    // Skip if no company data
    if (!companyData?.id) {
      setError('Company data is missing. Please try logging out and back in.');
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      let data;

      if (parsedDate && parsedHour !== null) {
        data = await getAppointmentsByDateAndHour(parsedDate, parsedHour, companyData.id);
      } else if (parsedDate) {
        data = await getAppointmentsByDate(parsedDate, companyData.id);
      } else {
        data = await getAllAppointments(companyData.id);
      }

      if (!data) {
        throw new Error('No data returned from appointment service');
      }
      
      setAppointments(data);
      
      // Extract unique calendar names
      const calendars = [...new Set(data.map(apt => apt.resource?.calendarName).filter(Boolean))];
      setAvailableCalendars(calendars);

      // Extract unique employees from appointment resources
      const employeeMap = new Map();
      data.forEach((apt) => {
        const employeeId = apt.resource?.employeeId || apt.employeeId;
        const employeeName = apt.resource?.employeeName || apt.employeeName || apt.resource?.calendarName;
        if (employeeId && employeeName && !employeeMap.has(employeeId)) {
          employeeMap.set(employeeId, employeeName);
        }
      });
      setAvailableEmployees(
        Array.from(employeeMap.entries()).map(([id, name]) => ({ id, name }))
      );
      
      setError(null);
    } catch (err) {
      setError(`Failed to load appointments: ${err.message}`);
      setAppointments([]); // Set empty array on error to prevent infinite loading
    } finally {
      setLoading(false);
      dataFetchedRef.current = true;
    }
  }, [parsedDate, parsedHour, companyData?.id]);
  
  // Fetch appointments based on URL parameters or all appointments
  useEffect(() => {
    dataFetchedRef.current = false;
    fetchAppointments();
  }, [fetchAppointments, companyData?.id, parsedDate, parsedHour]);
  
  // Calculate filtered appointments with useMemo to prevent unnecessary recalculations
  const filteredAppointmentsData = useMemo(() => {
    if (!appointments || !appointments.length) return [];
    
    let filtered = [...appointments];
    
    // Apply search term filter
    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      filtered = filtered.filter(apt => 
        (apt.title?.toLowerCase() || '').includes(term) || 
        (apt.resource?.calendarName?.toLowerCase() || '').includes(term) ||
        (apt.resource?.employeeName?.toLowerCase() || '').includes(term) ||
        (apt.resource?.status?.toLowerCase() || '').includes(term)
      );
    }
    
    // Apply calendar filter
    if (filterCalendar !== 'all') {
      filtered = filtered.filter(apt => 
        apt.resource?.calendarName === filterCalendar
      );
    }

    // Apply employee filter
    if (filterEmployee !== 'all') {
      filtered = filtered.filter((apt) => {
        const employeeId = apt.resource?.employeeId || apt.employeeId;
        return employeeId === filterEmployee;
      });
    }
    
    // Apply status filter
    if (filterStatus !== 'all') {
      filtered = filtered.filter(apt => 
        (apt.resource?.status?.toLowerCase() || '') === filterStatus.toLowerCase()
      );
    }
    
    // Apply sorting
    filtered.sort((a, b) => {
      let comparison = 0;
      
      switch (orderBy) {
        case 'start':
          comparison = (a.start?.getTime() || 0) - (b.start?.getTime() || 0);
          break;
        case 'title':
          comparison = (a.title || '').localeCompare(b.title || '');
          break;
        case 'calendar':
          comparison = (a.resource?.calendarName || '').localeCompare(b.resource?.calendarName || '');
          break;
        case 'status':
          comparison = (a.resource?.status || '').localeCompare(b.resource?.status || '');
          break;
        default:
          comparison = (a.start?.getTime() || 0) - (b.start?.getTime() || 0);
      }
      
      return order === 'asc' ? comparison : -comparison;
    });
    
    return filtered;
  }, [appointments, searchTerm, filterCalendar, filterEmployee, filterStatus, orderBy, order]);
  
  // Update filteredAppointments state when filteredAppointmentsData changes
  useEffect(() => {
    setFilteredAppointments(filteredAppointmentsData);
    setPage(1); // Reset to first page when filters change
  }, [filteredAppointmentsData]);
  
  // Calculate pagination with useMemo
  const paginatedAppointments = useMemo(() => {
    if (!filteredAppointments || filteredAppointments.length === 0) {
      return [];
    }
    
    const startIndex = (page - 1) * rowsPerPage;
    const endIndex = page * rowsPerPage;
    return filteredAppointments.slice(startIndex, endIndex);
  }, [filteredAppointments, page, rowsPerPage]);
  
  // Event handlers
  const handleBack = () => {
    navigate(-1);
  };
  
  const handleAppointmentClick = (appointment) => {
    const id = appointment.id || appointment.resource?.id;
    if (!id) return;
    const merged = {
      id,
      shortSlug: appointment.shortSlug ?? appointment.resource?.shortSlug,
      publicNumber: appointment.publicNumber ?? appointment.resource?.publicNumber
    };
    const appointmentSegment = getCompanyAppointmentUrlSegment(merged) || id;
    navigate(`/app/overview/appointment/${appointmentSegment}`, {
      state: {
        appointment: {
          ...appointment,
          id,
          dateTime: appointment.dateTime || appointment.start,
          ...(appointment.start && appointment.end
            ? { duration: Math.round((appointment.end - appointment.start) / (1000 * 60)) }
            : {})
        }
      }
    });
  };
  
  const handleRequestSort = (property) => {
    setOrderBy(property);
    setOrder(orderBy === property && order === 'asc' ? 'desc' : 'asc');
  };
  
  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };
  
  const handleChangeOrder = (newOrder) => {
    setOrder(newOrder);
  };
  
  const handleClearFilters = () => {
    setSearchTerm('');
    setFilterCalendar('all');
    setFilterEmployee('all');
    setFilterStatus('all');
  };
  
  // Format page title
  const pageTitle = formatAppointmentPageTitle(parsedDate, parsedHour);
  
  return (
    <Container maxWidth="xl" sx={{ py: 4 }}>
      {/* Header */}
      <AppointmentsHeader 
        title={pageTitle}
        onBack={handleBack}
      />
      
      {/* Filters */}
      <AppointmentsFilter 
        searchTerm={searchTerm}
        filterCalendar={filterCalendar}
        filterEmployee={filterEmployee}
        filterStatus={filterStatus}
        availableCalendars={availableCalendars}
        availableEmployees={availableEmployees}
        onSearchChange={setSearchTerm}
        onCalendarChange={setFilterCalendar}
        onEmployeeChange={setFilterEmployee}
        onStatusChange={setFilterStatus}
        onClearFilters={handleClearFilters}
      />
      
      {/* Appointments Table */}
      <AppointmentsTable 
        loading={loading}
        error={error}
        filteredAppointments={filteredAppointments}
        paginatedAppointments={paginatedAppointments}
        orderBy={orderBy}
        order={order}
        page={page}
        rowsPerPage={rowsPerPage}
        onRequestSort={handleRequestSort}
        onChangePage={handleChangePage}
        onChangeOrder={handleChangeOrder}
        onAppointmentClick={handleAppointmentClick}
        getStatusColor={getStatusColor}
      />
    </Container>
  );
};

export default AppointmentsView; 